import { axiosClientv1 } from "./apiClient";
import { ApiResponse } from "../Types";

export const GetTenants = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/system-admin/tenants`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load tenants.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const CreateTenant = async (formData: {
  name: string;
  tenant_sub_name: string;
  username: string;
  email: string;
  password: string;
  pos_client?: {
    client_name?: string;
    contact_number?: string;
    email?: string;
    address?: string;
  };
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/create`,
      formData,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to create tenant.",
      error: { code: error?.response?.status || 400, type: "bad_request" },
    };
  }
};

export const GetTenantById = async (id: string): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/system-admin/tenants`, {
      params: { id },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load tenant.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const UpdateTenantStatus = async (payload: {
  id: string;
  status: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/update-status`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to update tenant status.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const UpdateTenant = async (payload: {
  id: string;
  name?: string;
  tenant_sub_name?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/update`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to update tenant.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const GetTenantSubFeatures = async (
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/sub-features`,
      { id },
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load sub-features.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const UpdateTenantSubFeatures = async (payload: {
  id: string;
  sub_feature_id: string;
  is_enable: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/sub-features/update`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to update sub-feature.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const AssignTenantPackage = async (payload: {
  tenant_id: string;
  package_id: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/package/assign`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to assign package.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const UnassignTenantPackage = async (
  tenantId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/package/unassign`,
      { tenant_id: tenantId },
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to unassign package.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const GetTenantPackage = async (
  tenantId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/system-admin/tenants/package`, {
      tenant_id: tenantId,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load tenant package.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const GetTenantPackageHistory = async (
  tenantId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/package/history`,
      { tenant_id: tenantId },
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load package history.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const AddTenantPackageCharge = async (payload: {
  tenant_id: string;
  amount: number;
  description: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/package/charge`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to add charge.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const GetTenantPackageCharges = async (
  tenantId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/package/charges`,
      { tenant_id: tenantId },
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load charges.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const DeleteTenantPackageCharge = async (payload: {
  tenant_id: string;
  charge_id: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/package/charge/delete`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to remove charge.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const GetTenantBilling = async (
  tenantId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/billing`,
      { tenant_id: tenantId },
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load billing.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const GetTenantPayments = async (
  tenantId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/payments`,
      { tenant_id: tenantId },
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load payments.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const RecordTenantPayment = async (payload: {
  tenant_id: string;
  amount: number;
  payment_method?: string;
  reference?: string;
  note?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/payment/record`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to record payment.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const TerminateTenant = async (payload: {
  tenant_id: string;
  reason?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/terminate`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to terminate tenant.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const ReactivateTenant = async (payload: {
  tenant_id: string;
  payment?: {
    amount: number;
    payment_method?: string;
    reference?: string;
    note?: string;
  };
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/reactivate`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to reactivate tenant.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const GetTenantsBillingOverview = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/system-admin/tenants/billing/overview`,
      {},
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load billing overview.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};
